/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import { getFilterElementsDefault, useWorkplaceState } from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    changeTariffGroupItem,
    createTariffGroupItem,
    deleteTariffGroupItem,
    getTariffGroupExcel,
    getTariffGroupItem,
    getTariffGroups,
} from 'axios/tariffGroupApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    TariffGroup,
    TariffGroupChange,
    TariffGroupCreate,
    TariffGroupFilter as TariffGroupFilterType,
    TariffGroupTable,
} from 'models/api/tariffGroup';
import { useCallback, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { checkboxFormatter, deleteActionBtnFormatter } from 'utils/columnFormatters';
import TariffGroupAddButton from './TariffGroupAddButton';
import TariffGroupChangeModal from './TariffGroupChangeModal';
import TariffGroupFilter from './TariffGroupFilter';
import './TariffGroups.scss';

type TariffGroupsFormatterProps = FormatterProps<TariffGroupTable, TariffGroupTable[]>;

const TariffGroups = () => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const api = useApi();

    const queryClient = useQueryClient();

    const tariffsFormatter = useCallback(
        ({ row: { tariffGroupCreditProducts } }: TariffGroupsFormatterProps) => (
            <>
                {tariffGroupCreditProducts
                    .map(({ creditProduct = {} }) => creditProduct.name)
                    .join('; ')}
            </>
        ),
        []
    );

    return (
        <BOWorkplace<
            TariffGroupTable,
            TariffGroup,
            TariffGroupFilterType,
            TariffGroupChange,
            TariffGroupCreate
        >
            testId="tariff-groups"
            pageTitle="BackOfficeTariffGroups"
            filterParams={{
                id: null,
                name: null,
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey={'BackOfficeId'} />,
                    width: '5%',
                },
                {
                    key: 'isActive',
                    name: <Trans i18nKey={'BackOfficeIsActive'} />,
                    cellClass: 'pt-2 cell-centered',
                    headerCellClass: 'cell-centered',
                    width: '5%',
                    formatter: checkboxFormatter('isActive'),
                },
                {
                    key: 'name',
                    name: <Trans i18nKey={'BackOfficeName'} />,
                    width: '15%',
                },
                {
                    key: 'tariffs',
                    name: <Trans i18nKey={'BackOfficeTariffs'} />,
                    formatter: tariffsFormatter,
                },
                {
                    key: 'dsc',
                    name: <Trans i18nKey={'BackOfficeDescription'} />,
                    width: '15%',
                },
                {
                    key: 'actions',
                    name: (
                        <div className="text-center">
                            <FontAwesomeIcon icon={faBoltLightning} />
                        </div>
                    ),
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: '5%',
                    notHidden: true,
                    visible: hasRole('tariffgroupsDelete'),
                    formatter: deleteActionBtnFormatter(hasRole('tariffgroupsDelete')),
                },
            ]}
            storagePath="tariffGroups/columns"
            fetchQueryFn={(filter) => getTariffGroups(api, filter)}
            fetchFormat={(dataObject) => dataObject}
            buttons={
                hasRole('tariffgroupsCreate')
                    ? [<TariffGroupAddButton key="1" showModal={() => setIsModalVisible(true)} />]
                    : []
            }
            getFilterElements={getFilterElementsDefault([
                { name: t('BackOfficeId'), id: 'id' },
                { name: t('BackOfficeName'), id: 'name' },
            ])}
            filter={<TariffGroupFilter />}
            showModal={hasRole('tariffgroupsGetById') ? () => setIsModalVisible(true) : undefined}
            modal={{
                component: isModalVisible ? (
                    <TariffGroupChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ) : (
                    <></>
                ),
                changeMutationFn: (item) => changeTariffGroupItem(api, item),
                addMutationFn: (item) => createTariffGroupItem(api, item),
                fetchCurrentItemQueryFn: (id) => getTariffGroupItem(api, parseInt(`${id}`)),
            }}
            deleteMutationFn={(id) => deleteTariffGroupItem(api, parseInt(`${id}`))}
            queryKey={['tariffGroup']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getTariffGroupExcel(api, filter)}
            showExcelBtn={hasRole('tariffgroupsExcel')}
        />
    );
};

const useTariffGroupsWorkplaceState = () =>
    useWorkplaceState<TariffGroupTable, TariffGroup, TariffGroupChange, TariffGroupCreate>();

export default TariffGroups;
export { useTariffGroupsWorkplaceState };
